import { addDays, format } from 'date-fns'
import { ZodTypeAny, z } from 'zod'

export enum CampaignStatus {
  draft = 'draft',
  creating = 'creating', // TODO: Remove — no backend code sets this status. Verify no production campaigns have this value before dropping it via migration.
  testing = 'testing',
  readyToCreate = 'readyToCreate',
  scheduled = 'scheduled',
  active = 'active',
  deleting = 'deleting',
  completed = 'completed',
}

const dateString = z.string().min(1, 'required')

const dateParsed = dateString
  .refine((value) => /^[0-9]{4}-[0-9]{2}-[0-9]{2}$/.test(value), 'invalid_date')
  .transform((d: string) => new Date(d))

const DateType = {
  startDate: dateParsed,
  endDate: dateParsed,
}

const WithId = { id: z.string().uuid() }

const radioType = z.preprocess(
  (value) => (value ? value : ''),
  z.string().min(1, 'required'),
)

const BaseSchema = z.object({
  typeOfCampaign: radioType,
  name: z.string().min(1, 'required'),
  companyName: z.string().min(1, 'required'),
  startDate: dateString,
  endDate: dateString,
  details: z.string().min(1, 'required'),
  senderAddress: z.string().min(1, 'required'),
  referralCampaign: z.boolean(),
  emailAddress: z.string(),
  privacyLink: z.string().url({
    message: 'invalid_url',
  }),
  campaignCategory: z.enum(['generic', 'birthday', 'lottery'], {
    required_error: 'required',
    invalid_type_error: 'required',
  }),
  customerId: z.string().min(1, 'required'),
  campaignGroupId: z.string().nullable().optional(),
  ecFlowTexts: z.any(),
  scheduleDateTime: z
    .union([
      z.null(),
      z.undefined(),
      z.string().datetime({ message: 'Invalid datetime string! Must be UTC.' }),
    ])
    .optional(),
})

const withEffects = (schema: ZodTypeAny) =>
  schema
    .refine(
      (schema) =>
        new Date(schema.startDate) < addDays(new Date(schema.endDate), 1),
      {
        path: ['dates_error'],
        message: 'campaign_length_too_short',
      },
    )
    .refine((schema) => addDays(new Date(schema.endDate), 1) > new Date(), {
      path: ['dates_error'],
      message: 'campaign_end_date_in_the_past',
    })

const CreateCampaignSchema = withEffects(BaseSchema)
const CreateCampaignTypedSchema = withEffects(BaseSchema.extend(DateType))
const UpdateCampaignDetailsSchema = withEffects(
  BaseSchema.extend(WithId)
    .required()
    .extend({
      campaignGroupId: z.string().nullable().optional(),
      privacyLink: z.string().url({ message: 'invalid_url' }),
    }),
)
const CampaignDetailsFormSchema = withEffects(
  BaseSchema.extend({
    startDate: z
      .string()
      .transform((d: string) => (d ? format(new Date(d), 'yyyy-MM-dd') : d)),
    endDate: z
      .string()
      .transform((d: string) => (d ? format(new Date(d), 'yyyy-MM-dd') : d)),
  })
    .required()
    .extend({
      campaignGroupId: z.string().nullable().optional(),
      privacyLink: z.string().url({ message: 'invalid_url' }),
    }),
)

const UpdateCampaignDetailsTypedSchema = withEffects(
  BaseSchema.extend(WithId)
    .extend(DateType)
    .required()
    .extend({
      campaignGroupId: z.string().nullable().optional(),
      privacyLink: z.string().url({ message: 'invalid_url' }),
    }),
)

export type CreateCampaignType = z.infer<typeof CreateCampaignSchema>
export type UpdateCampaignDetailsType = z.infer<
  typeof UpdateCampaignDetailsSchema
>

export {
  CreateCampaignSchema,
  CreateCampaignTypedSchema,
  UpdateCampaignDetailsSchema,
  UpdateCampaignDetailsTypedSchema,
  CampaignDetailsFormSchema,
}
